import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { setFloorPlateGallery } from '@store/actionSlices/floorPlateGallery'
import { BucketSource, RootStateTypeExtra } from '@store/types'

import {
  FloorPlateGalleryData,
  selectFromResult,
  useGetFloorPlateGalleryQuery,
} from '@api/floor-plate-gallery'

import { FileType } from '@adUtilities/constants'
import { ProjectType } from '@adUtilities/constants/common'

const useGetFloorPlateGalleries = () => {
  const dispatch = useDispatch()

  const {
    projectName,
    floorPlateGallery,
    activeBlock,
    activeLevel,
    projectType,
    activePrecinctId,
    activeStageId,
  } = useSelector((state: RootStateTypeExtra) => ({
    projectName: state.projectIdentity.projectName,
    projectType: state.projectConfig.type,
    floorPlateGallery: state.floorPlateGallery,
    activeBlock: state.building.activeBlock,
    activeLevel: state.building.activeLevel,
    activePrecinctId: state.houseAndLand.activePrecinctId,
    activeStageId: state.houseAndLand.activeStageId,
  }))

  const { isLoaded, floorPlateGalleryData } = useGetFloorPlateGalleryQuery(
    { projectName },
    {
      selectFromResult,
    }
  )

  const matchHouseAndLand = (value: FloorPlateGalleryData) => {
    if (activePrecinctId) {
      return (
        (value?.precinct || '').toString() === activePrecinctId &&
        (value?.stage || '').toString() === activeStageId
      )
    }

    return (value?.stage || '').toString() === activeStageId
  }

  const matchApartment = (value: FloorPlateGalleryData) => {
    if (activeBlock) {
      return (
        (value?.block || '').toString() === activeBlock &&
        (value?.level || '').toString() === activeLevel
      )
    }

    return (value?.level || '').toString() === activeLevel
  }

  const slides = useMemo(() => {
    if (floorPlateGallery.length < 1) {
      return []
    }

    const foundData = floorPlateGallery.find((value) => {
      if (projectType === ProjectType.HouseAndLand) {
        return matchHouseAndLand(value)
      }

      return matchApartment(value)
    })

    if (foundData === null) {
      return []
    }

    const floorAssets = foundData?.assets || []

    if (floorAssets.length === 0) {
      return []
    }

    return floorAssets.map((item) => ({
      id: item.id,
      src: item.imageSrc || '',
      videoSource: item.videoSrc,
      thumbnail: item.imageSrc,
      fileType: item.type as FileType,
      bucketType: BucketSource.Legacy,
      noSpliceUrl: false,
    }))
  }, [
    activeLevel,
    activeBlock,
    activePrecinctId,
    activeStageId,
    floorPlateGallery,
  ])

  useEffect(() => {
    if (floorPlateGallery.length === 0 && floorPlateGalleryData.length > 0) {
      dispatch(setFloorPlateGallery(floorPlateGalleryData))
    }
  }, [isLoaded, floorPlateGallery, floorPlateGallery])

  return {
    slides,
    isLoaded,
  }
}

export default useGetFloorPlateGalleries
